import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import isNull from 'lodash/isNull';
import { parse as parseDate } from 'date-fns';

import { Loader, BlurredContactAdvisorBox } from 'ui';
import { SummaryCard, HeadSquare, SummaryCardLineChart } from 'modules/common/SummaryCard';
import { SummaryCardRequestCreditReview } from 'modules/creditReview';
import { fetchCreditReviewChartsData, fetchCreditReviewSummaryTab } from 'services';
import { useHeadSquareStyles, useStyles } from 'shared/styles/summaryCard';
import { summaryCardHistoryParser } from 'shared/parsers';
import { generalInformationCreditReview } from 'shared/constants/summaryTab';
import { creditRatingScalesMapping } from 'shared/mappings';
import { ratingConstants } from 'shared/constants/company';
import { portfolioTypes } from 'shared/constants/portfolioTypes';
import { useCofaceContract } from 'shared/hooks/useCofaceContract/useCofaceContract';
import { cofaceAccessTypes } from 'shared/constants/creditReview/cofaceAccessTypes';
import { creditReviewRequestTooltips } from 'shared/constants/creditReview/creditReviewRequestTooltips';
import { formatDate } from 'shared/functions';
import { datesLabelsCreditReview } from 'shared/constants/summaryTab/datesLabels';
import { creditReviewDocumentsParser } from 'shared/parsers/creditReviewDocumentsParser';
import { theme } from '../../theme';

const CreditReview = ({
  ratingAvailable,
  entity,
  easyNumber,
  isPending,
  isRequestRatingPending,
  handleRatingRequest,
}) => {
  const { t } = useTranslation();
  const headSquareClasses = useHeadSquareStyles();
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({});
  const [documents, setDocuments] = useState([]);
  const [historyData, setHistoryData] = useState();
  const [latestChangeData, setLatestChangeData] = useState({});
  const [wasRequested, setWasRequested] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);
  const { orangeAccent } = theme.palette.custom;

  const { cofaceAccessType } = useCofaceContract();
  const isRequestAvailable = cofaceAccessType === cofaceAccessTypes.REQUEST_ALLOWED;
  const contactAdvisorText = creditReviewRequestTooltips[cofaceAccessType];

  const fetchCreditReview = () => {
    setIsLoading(true);
    fetchCreditReviewSummaryTab(easyNumber)
      .then((result) => {
        setData(result);
        setIsLoading(false);
        setWasRequested(result.wasRequested || false);
        setIsAvailable(true);
      })
      .catch(() => {
        setIsAvailable(false);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (easyNumber) {
      fetchCreditReviewChartsData(easyNumber).then((scoreHistoryData) => {
        if (scoreHistoryData) {
          setLatestChangeData({
            date: scoreHistoryData.latest_change_date,
            initialScore: scoreHistoryData.first_score
              ? parseDate(scoreHistoryData.first_score, 'dd/MM/yyyy', new Date())
              : undefined,
            status: scoreHistoryData.latest_change_status,
          });
          setHistoryData({
            labels: scoreHistoryData.score_over_time_labels,
            data: scoreHistoryData.score_over_time_values,
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [easyNumber]);

  useEffect(() => {
    const dataAvailable = !!(entity && entity.requested);
    setData(dataAvailable ? entity.requested : {});
    setDocuments(creditReviewDocumentsParser(entity.docs || []));
    setIsAvailable(dataAvailable);
    setIsLoading(false);
  }, [entity]);

  const showCreditReviewSummaryCard = isLoading || isAvailable;

  return (
    <>
      <Box mb={2}>
        {ratingAvailable && easyNumber ? (
          <Box position="relative">
            {isPending && <Loader cover />}

            {showCreditReviewSummaryCard ? (
              <SummaryCard
                headSquare={
                  <HeadSquare title={t('Credit Review')}>
                    <Typography
                      color={
                        generalInformationCreditReview.includes(
                          'Unsolicited' && 'Without entity participation',
                        )
                          ? orangeAccent
                          : ''
                      }
                    >
                      <span className={headSquareClasses.score}>{data.score}</span>
                      <span className={headSquareClasses.max}>/10</span>
                      {data && !isNull(data.probability_of_default) && (
                        <Tooltip title={data.probability_of_default_definition || ''}>
                          <Typography className={classes.probabilityOfDefault}>
                            {t('PD')} : {data.probability_of_default}%
                          </Typography>
                        </Tooltip>
                      )}
                    </Typography>
                  </HeadSquare>
                }
                history={<SummaryCardLineChart history={summaryCardHistoryParser(historyData)} />}
                className={classes.summaryCard}
                isLoading={isLoading}
                data={{
                  generalInformationList: generalInformationCreditReview,
                  additionalInformation: {
                    countryRisk: data.country_risk_assessment,
                  },
                  ratingScalesData: creditRatingScalesMapping(
                    portfolioTypes.CREDIT_REVIEW,
                    ratingConstants.entityReview,
                  ),
                  levels: { level: 'L2' },
                  latestChange: {
                    date: formatDate(latestChangeData.date, 'd MMMM yyyy'),
                    status: latestChangeData.status,
                  },
                  dates: {
                    initialScore: formatDate(latestChangeData.initialScore),
                    latestChange: formatDate(latestChangeData.date),
                  },
                  datesLabels: datesLabelsCreditReview,
                  documents,
                  ...data,
                }}
              />
            ) : (
              <div className={classes.summaryCard}>
                <SummaryCardRequestCreditReview
                  easyNumber={data.easyNumber}
                  onRequest={fetchCreditReview}
                  isAvailable={isAvailable}
                  wasRequested={wasRequested}
                />
              </div>
            )}

            {/* TODO: Uncomment when peer group comparison is available
            <Box mb={2}>
              <Paper>
                <Box pb={2}>
                  <Box p={2}>
                    <Typography variant="h5">{t('Assessment over time')}</Typography>
                  </Box>
                  <Divider />
                  <ScoreHistoryWidget
                    easyNumber={easyNumber}
                    entityName={entity.entityName || entity.name}
                    industry={entity.sector}
                  />
                </Box>
              </Paper>
            </Box> */}
          </Box>
        ) : (
          <BlurredContactAdvisorBox
            isPending={isRequestRatingPending}
            handleRatingRequest={isRequestAvailable ? handleRatingRequest : null}
            contactAdvisor={contactAdvisorText}
          />
        )}
      </Box>
    </>
  );
};

CreditReview.propTypes = {
  ratingAvailable: PropTypes.bool.isRequired,
  entity: PropTypes.object.isRequired,
  easyNumber: PropTypes.string.isRequired,
  isPending: PropTypes.bool.isRequired,
  isRequestRatingPending: PropTypes.bool.isRequired,
  handleRatingRequest: PropTypes.func.isRequired,
};

export default CreditReview;
